import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { useAuth } from '../context/AuthContext';
import { getMyOrders } from '../services/orderApi';

const MyAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getLocalizedPath } = useLanguage();
  const { user } = useAuth();
  const userName = user?.firstName || 'User';
  const [recentOrder, setRecentOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentOrder = async () => {
      try {
        const result = await getMyOrders(new URLSearchParams({ limit: '1' }));
        const orders = Array.isArray(result) ? result : result.data || [];
        if (orders.length > 0) {
          setRecentOrder(orders[0]);
        }
      } catch (error) {
        console.error('Failed to fetch recent orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecentOrder();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh] mt-[78px]">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="mt-[78px] px-4 sm:px-6 lg:px-8">
      <div className="max-w-[856px] mx-auto pt-8 sm:pt-[92px] pb-[166px] sm:pb-[100px]">
        <div className="space-y-5">
          <h2 className="text-black text-[32px] leading-[40px] tracking-[-1.25px] font-normal libre-baskerville">
            {t('account.title')}
          </h2>
          <h3 className="text-[24px] leading-[30px] font-medium text-black tracking-[-1.25px]">
            {t('account.hello', { name: userName })}
          </h3>
          <p className="text-[#777777] text-[16px] leading-[20px] font-normal tracking-[-1.25px]">
            {t('account.description')}
          </p>
        </div>
        <div className="my-10 grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div className="space-y-5">
            <h3 className="text-black text-[20px] leading-[25px] font-medium tracking-[-1.25px]">
              {user
                ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                : userName}
            </h3>
            <p className="text-[#777777] text-[16px] leading-[20px] font-normal tracking-[-1.25px]">
              {user?.email || ''}
            </p>
          </div>
          <div className="space-y-5">
            <h3 className="text-black text-[20px] leading-[25px] font-medium tracking-[-1.25px]">
              {t('account.defaultAddress')}
            </h3>
            <p className="text-[#777777] text-[16px] leading-[20px] font-normal tracking-[-1.25px]">
              {user
                ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                : userName}{' '}
              {user?.country || ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate(getLocalizedPath('/profile'))}
          className="bg-black h-[54px] text-white text-[13px] leading-[14px] font-medium uppercase px-10"
        >
          {t('account.editDetails')}
        </button>
        {recentOrder && (
          <div className="mt-10">
            <h3 className="text-black text-[20px] leading-[25px] tracking-[-1.25px] font-medium">
              {t('account.recentOrders')}
            </h3>
            <div className="w-full border-y border-[#AFAFAF] py-5 mt-5">
              <div className="max-w-[684px] w-full flex items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-[119px] h-[119px] bg-[#D9D9D9] flex items-center justify-center overflow-hidden p-1 rounded">
                    <img
                      src={
                        recentOrder.items?.[0]?.image ||
                        '/assets/product-image.webp'
                      }
                      alt="product"
                      className="object-fit"
                    />
                  </div>
                  <h2 className="text-black text-[20px] leading-[27px] font-semibold">
                    {recentOrder.items?.[0]?.productName ||
                      `Order #${recentOrder.orderNumber}`}
                  </h2>
                </div>
                <h2 className="text-black text-[18px] leading-[25px] font-medium">
                  {recentOrder.currency || '$'}
                  {Number(recentOrder.totalAmount).toFixed(2)}
                </h2>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyAccount;
